import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';

import { Button } from '@repo/ui/components/button';
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
  useForm,
} from '@repo/ui/components/form';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { CheckBoxField } from '@repo/ui/components/check-box-field';
import { TextField } from '@repo/ui/components/text-field';
import { NumericFormatField } from '@repo/ui/components/numeric-format-field';
import { Info } from '@repo/ui/components/icon';

import SelectDropdown from '~/components/select-dropdown';
import { MultiSelectField } from '@repo/ui/components/form-field';
import { useDataDropdown } from '~/utils/home';
import { Icon } from '~/components/ui/icon';

const formSchema = (tCommon: (key: string, options?: any) => string) =>
  z.object({
    provinceId: z.string().optional(),
    categoryId: z.array(z.string()).optional(),
    priceRange: z.string().optional(),
    area: z.string().optional(),
    name: z
      .string(tCommon("error.required"))
      .min(1, tCommon("error.required"))
      .max(100, tCommon("error.max", { max: 100 })),
    phone: z
      .string(tCommon("error.required"))
      .min(1, tCommon("error.required"))
      .max(11, tCommon("error.max", { max: 11 }))
      .regex(/^\d+$/, tCommon("error.invalid_phone")),
    email: z
      .string()
      .email(tCommon("error.invalid_email"))
      .optional()
      .or(z.literal("")),
    address: z.coerce.string(tCommon("error.required")),
    agree: z.boolean().refine((v) => v === true, tCommon("error.must_accept")),
  });

function ContactFormContent({ errorMsg }: { errorMsg: string | null }) {
  const { t } = useTranslation('home');
  return (
    <DialogContent className="w-full max-w-[900px] max-h-[95vh] overflow-scroll">
      <DialogHeader>
        <DialogTitle className="text-3xl font-bold font-helve">
          {t("search_box.consign_form.contact_form.title")}
        </DialogTitle>
        <Alert className="border-0 bg-primary-50 text-primary">
          <Info size={20} />
          <AlertDescription className="text-black">
            {t("search_box.consign_form.contact_form.note")}
          </AlertDescription>
        </Alert>
        {errorMsg ? (
          <Alert variant="destructive">
            <AlertDescription>{errorMsg}</AlertDescription>
          </Alert>
        ) : null}
      </DialogHeader>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <TextField
          name="name"
          label={t(
            "search_box.consign_form.contact_form.fields.full_name.label"
          )}
          placeholder={t(
            "search_box.consign_form.contact_form.fields.full_name.placeholder"
          )}
        />
        <TextField name="email" label="Email" placeholder="Email" />
        <TextField
          name="phone"
          label={t("search_box.consign_form.contact_form.fields.phone.label")}
          placeholder={t(
            "search_box.consign_form.contact_form.fields.phone.placeholder"
          )}
        />
        <TextField name="address" label={"Tỉnh/xã"} placeholder={"Nhập"} />
      </div>
      <CheckBoxField
        name="agree"
        label={
          <Trans
            ns="home"
            i18nKey={"search_box.consign_form.contact_form.checkbox.policy"}
            components={{
              span: <span className="text-primary" />,
              p: <p className="hover:cursor-pointer" />,
            }}
          />
        }
      />
      <div className="flex gap-2">
        <Icon name="shield" />
        <p className="text-sm">
          {t("search_box.consign_form.contact_form.privacy")}
        </p>
      </div>

      <Button type="submit" form="find-house-form">
        {t("search_box.consign_form.contact_form.submit")}
      </Button>
    </DialogContent>
  );
}

function FormFindHouse() {
  const { province, category } = useDataDropdown();
  const { t } = useTranslation('home');
  const { t: tCommon } = useTranslation('common');
  const fetcher = useFetcher();

  const form = useForm({
    resolver: zodResolver(formSchema(tCommon as any)),
    defaultValues: {
      area: '',
      categoryId: [] as string[],
      priceRange: '',
      provinceId: '',
      name: '',
      phone: '',
      email: '',
      address: '',
      agree: false,
    },
    mode: 'onChange',
  });

  const [open, setOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [successOpen, setSuccessOpen] = useState(false);

  const listProvince = useMemo(() => province, [province, t]);

  const onSubmit = useCallback((values: any) => {
    const payload = {
      formType: 'propertyConsult',
      ...values,
    };
    fetcher.submit(payload as any, {
      method: 'post',
      encType: 'application/json',
    });
  }, [fetcher]);

  const handleCloseModal = useCallback(
    (value: boolean) => {
      setErrorMsg(null);
      setOpen(value);
      if (!value) {
        form.reset({
          ...form.getValues(),
          name: "",
          phone: "",
          email: "",
          address: "",
          agree: false,
        });
      }
    },
    [form],
  );

  const handleValidateField = useCallback(() => {
    const values = form.getValues();
    const requiredKeys: Array<{ key: 'provinceId' | 'priceRange' | 'area'; isEmpty: (v: string) => boolean }> = [
      { key: 'provinceId', isEmpty: (v) => !v },
      { key: 'priceRange', isEmpty: (v) => !v },
      { key: 'area', isEmpty: (v) => !v },
    ];

    let ok = true;

    requiredKeys.forEach(({ key, isEmpty }) => {
      const value:any = values[key];
      if (isEmpty(value)) {
        form.setError(key, { type: 'required', message: tCommon('error.required') });
        ok = false;
      } else {
        form.clearErrors(key);
      }
    });

    if (!Array.isArray(values.categoryId) || values.categoryId.length === 0) {
      form.setError('categoryId', { type: 'required', message: tCommon('error.required') });
      ok = false;
    } else {
      form.clearErrors('categoryId');
    }

    if (ok) {
      setOpen(true);
    }
  }, [form, tCommon]);

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      if (!fetcher.data.ok) {
        setSuccessMsg(null);
        setErrorMsg('Gửi không thành công. Vui lòng thử lại.');
      } else {
        setErrorMsg(null);
        setSuccessMsg('Đăng ký thành công. Chúng tôi sẽ liên hệ sớm nhất!');
        setOpen(false);
        setSuccessOpen(true);
        form.reset();
      }
    }
  }, [fetcher.state, fetcher.data, form]);

  return (
    <Form {...form}>
      <form
        className="grid gap-5"
        id="find-house-form"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="grid flex-1 grid-cols-6 gap-5">
          <div className="col-span-3">
            <FormField
              name="provinceId"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <SelectDropdown
                    isControlled
                    {...field}
                    defaultValue={field.value}
                    onValueChange={field.onChange}
                    items={listProvince}
                    label={t("search_box.find_form.filters.region")}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="col-span-3">
            <MultiSelectField
              name="categoryId"
              listOption={category}
              label={t("search_box.find_form.filters.real_estate_type")}
            />
          </div>
          <div className="col-span-3 md:col-span-2">
            <NumericFormatField
              name="priceRange"
              inputMode="numeric"
              label={t("search_box.find_form.filters.price")}
              variant="filled"
              numericFormatProps={{
                thousandSeparator: true,
              }}
            />
          </div>
          <div className="col-span-3 md:col-span-2">
            <NumericFormatField
              name="area"
              inputMode="numeric"
              label={t("search_box.find_form.filters.area")}
              variant="filled"
              numericFormatProps={{
                thousandSeparator: true,
              }}
            />
          </div>
          <div className="col-span-6 md:col-span-2">
            <Button
              className="w-full h-12 text-white md:col-span-1 bg-second hover:bg-second/80"
              type="button"
              onClick={handleValidateField}
            >
              {t("search_box.consign_form.submit")}
            </Button>
          </div>
        </div>
        <Dialog open={open} onOpenChange={handleCloseModal}>
          <ContactFormContent errorMsg={errorMsg} />
        </Dialog>
        <Dialog
          open={successOpen}
          onOpenChange={(value) => {
            setSuccessOpen(value);
            if (!value) {
              setSuccessMsg(null);
              form.reset();
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {t("search_box.consign_form.dialog.title")}
              </DialogTitle>
            </DialogHeader>
            <Alert className="border-0 bg-primary-50 text-primary">
              <AlertDescription>
                {successMsg || t("search_box.consign_form.messages.success")}
              </AlertDescription>
            </Alert>
            <div className="flex justify-end">
              <Button onClick={() => setSuccessOpen(false)}>
                {t("search_box.consign_form.actions.close")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </form>
    </Form>
  );
}

export default FormFindHouse;
